/**
 * 鉴权上下文 · 全局可访问当前登录用户
 * - 启动时从前端存储恢复
 * - login / logout 暴露给页面
 * - 监听 poet:unauthorized 事件（来自 http 拦截器），自动清空并跳登录
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import * as authApi from '@/api/auth';
import { queryClient } from '@/api/queryClient';
import {
  clearAuth,
  getAccessToken,
  getLastActivity,
  getStoredUser,
  setAuth as persistAuth,
  setLastActivity,
  IDLE_TIMEOUT_MS,
  type StoredUser,
} from './storage';

interface AuthContextValue {
  user: StoredUser | null;
  isAuthenticated: boolean;
  login: (account: string, password: string, accountType: 'phone' | 'studentId' | 'employeeId') => Promise<{
    mustChangePassword: boolean;
    user: StoredUser;
  }>;
  register: (phone: string, password: string, name: string) => Promise<{ user: StoredUser }>;
  logout: () => Promise<void>;
  setUserLocal: (user: StoredUser) => void;
}

const Ctx = createContext<AuthContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<StoredUser | null>(() => {
    if (!getAccessToken()) return null;
    return getStoredUser();
  });

  // 监听 http 拦截器派发的未授权事件
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener('poet:unauthorized', handler);
    return () => window.removeEventListener('poet:unauthorized', handler);
  }, []);

  // 闲置超时：记录用户操作时间，连续 IDLE_TIMEOUT_MS 无操作则强制登出。
  // 活跃用户会被 http 拦截器自动续期（不掉线）；只有真正闲置才失效。
  useEffect(() => {
    if (!user) return;
    setLastActivity(Date.now()); // 登录/恢复会话即视为一次活跃

    let throttleAt = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - throttleAt > 30_000) {
        throttleAt = now;
        setLastActivity(now);
      }
    };
    const events: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    const timer = window.setInterval(() => {
      const last = getLastActivity();
      if (last && Date.now() - last > IDLE_TIMEOUT_MS) {
        // 闲置超时 → 标记原因供登录页提示，清空会话
        sessionStorage.setItem('poet.sessionExpiredReason', 'idle');
        clearAuth();
        setUser(null);
      }
    }, 30_000);

    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      window.clearInterval(timer);
    };
  }, [user]);

  const login = useCallback(
    async (account: string, password: string, accountType: 'phone' | 'studentId' | 'employeeId') => {
      const res = await authApi.login({ account, password, accountType });
      persistAuth({
        accessToken: res.tokens.accessToken,
        refreshToken: res.tokens.refreshToken,
        user: res.user,
      });
      // 切换账号：清空上一个用户残留的缓存，避免串显其他人的进度/数据
      queryClient.clear();
      setUser(res.user);
      return { mustChangePassword: res.mustChangePassword, user: res.user };
    },
    [],
  );

  const register = useCallback(
    async (phone: string, password: string, name: string) => {
      const res = await authApi.register({ phone, password, name });
      persistAuth({
        accessToken: res.tokens.accessToken,
        refreshToken: res.tokens.refreshToken,
        user: res.user,
      });
      queryClient.clear();
      setUser(res.user);
      return { user: res.user };
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // 即使后端登出失败也本地清空
    }
    clearAuth();
    queryClient.clear();
    setUser(null);
  }, []);

  const setUserLocal = useCallback((u: StoredUser) => {
    setUser(u);
    const raw = localStorage.getItem('poet.user');
    if (raw) {
      localStorage.setItem('poet.user', JSON.stringify(u));
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: !!user, login, register, logout, setUserLocal }),
    [user, login, register, logout, setUserLocal],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
